import { useCallback, useContext, useEffect, useMemo } from "react";
import { UserContext } from "../contexts/UserContext";
import { getOrganizations } from "../../services/organizationService";
import { DASHBOARD_PERMISSION_CODES, hasPermission } from "../permissions";
import useOrgScopeStore from "../stores/useOrgScopeStore";
import { MAX_PAGE_SIZE } from "../constants/constants";
import { childrenIndex, subtreeUuids } from "../helpers/orgTree";

// Org-subtree selection for the school analytics pages. Anyone who can see the
// school dashboard fetches the organizations in their scope; whether they can
// *compare/switch* schools is derived from that scope — you can compare iff
// your scope spans more than one org (head-office staff, multi-school admins,
// superusers). A single-school admin gets one org and no selector. There is no
// separate permission for this. `effectiveOrgUuid` is what pages pass as the
// `?organization=` query param — backend scoping rejects anything outside the
// caller's subtree, so this is a navigation affordance, not access control.
export default function useOrgScope() {
  const { user } = useContext(UserContext);
  const selectedOrgUuid = useOrgScopeStore((s) => s.selectedOrgUuid);
  const setSelectedOrgUuid = useOrgScopeStore((s) => s.setSelectedOrgUuid);
  const organizations = useOrgScopeStore((s) => s.organizations);
  const loaded = useOrgScopeStore((s) => s.loaded);
  const setOrganizations = useOrgScopeStore((s) => s.setOrganizations);

  const canViewSchool = hasPermission(
    user,
    DASHBOARD_PERMISSION_CODES.SCHOOL_VIEW,
  );
  // Scope decides reach: more than one org in scope ⇒ this user oversees a
  // network (or several schools) and may switch/compare between them.
  const canCompareSchools = organizations.length > 1;

  // Parent -> children over the loaded subtree, for the questions below and the
  // pages (`subtreeOf`) that need "what is under this org".
  const childrenOf = useMemo(() => childrenIndex(organizations), [organizations]);
  const subtreeOf = useCallback((uuid) => subtreeUuids(childrenOf, uuid), [childrenOf]);

  // Where the analytics open before anyone picks: of the caller's current
  // links, the one heading the most of the loaded subtree. A regional
  // coordinator holds the school they are filed at AND the region they
  // oversee, and the region is the job — while `organizations[0]` is the
  // school on purpose, being "my school" to the teacher's own screens. One
  // link (everyone else) or a subtree not loaded yet: simply the first link.
  const primaryOrgUuid = useMemo(() => {
    const links = user?.organizations || [];
    const own = links
      .filter((l) => l.is_in_progress)
      .map((l) => l.organization?.uuid)
      .filter(Boolean);
    if (!own.length) return links[0]?.organization?.uuid || null;
    return own.reduce((best, uuid) =>
      subtreeOf(uuid).length > subtreeOf(best).length ? uuid : best,
    );
  }, [user, subtreeOf]);

  // Fetch the caller's full org subtree once per session (cached in the store,
  // so navigating between the analytics pages reuses it). `page_size` pulls the
  // whole subtree in one request — the selector must offer every descendant,
  // not just the first page.
  useEffect(() => {
    if (!canViewSchool || loaded) return undefined;
    const controller = new AbortController();
    getOrganizations({
      params: { mine: true, page_size: MAX_PAGE_SIZE },
      signal: controller.signal,
    })
      .then((data) => setOrganizations(data?.results || []))
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        setOrganizations([]);
      });
    return () => controller.abort();
  }, [canViewSchool, loaded, setOrganizations]);

  // Once the scope list has loaded, drop a stale selection so we never query an
  // org the user can't see: a single-school user who inherited one (e.g. after
  // a user switch), or a selection that left the subtree (hierarchy edited in
  // admin).
  useEffect(() => {
    if (!loaded || !selectedOrgUuid) return;
    if (!canCompareSchools || !organizations.some((o) => o.uuid === selectedOrgUuid)) {
      setSelectedOrgUuid(null);
    }
  }, [canCompareSchools, loaded, selectedOrgUuid, organizations, setSelectedOrgUuid]);

  // primaryOrgUuid is null for a compare-capable account with no staff link
  // (e.g. a bare superuser); fall back to the first in-scope org so dashboards
  // resolve instead of hanging.
  const effectiveOrgUuid =
    selectedOrgUuid || primaryOrgUuid || organizations[0]?.uuid || null;

  // What a roster passes as `?organization=`: the school the user actually
  // PICKED, and only once the loaded subtree vouches for it. Null means no
  // pick — "everyone I can see".
  const pickedOrgUuid =
    canCompareSchools && organizations.some((o) => o.uuid === selectedOrgUuid)
      ? selectedOrgUuid
      : null;
  // A pick restored from sessionStorage can't be vouched for until the subtree
  // lands, and a roster fetched before then is the whole network's, thrown
  // away a moment later — callers wait while this is true. Never true without
  // school.view: that subtree is never loaded, so waiting on it would hang.
  const pickPending = Boolean(selectedOrgUuid) && canViewSchool && !loaded;

  // Whether this scope rolls up a network, decided once here because the
  // analytics pages branch their whole fetch on it — before the fetch, so the
  // answer cannot come from the payload it decides against.
  //
  // Counts SCHOOLS in the selected subtree the way the backend's `school_count`
  // does: an org with no child of its own in that subtree. Counting direct
  // children instead would call a head office over one school a network (it has
  // a child) and a head over regions a single school (it has one child), and
  // either way the page and the API would gate differently on the same scope.
  const isNetwork = useMemo(() => {
    if (!effectiveOrgUuid) return false;
    const schools = subtreeOf(effectiveOrgUuid).filter((uuid) => !childrenOf.has(uuid));
    return schools.length > 1;
  }, [childrenOf, subtreeOf, effectiveOrgUuid]);

  // The single answer a page branches its fetch on, so no page can assemble
  // three of its own and get one of them wrong:
  //   "loading"     — the subtree is still unknown, and until it lands every
  //                   account looks like a single school, which is exactly how
  //                   a network-sized roster request escapes. Ask for nothing.
  //   "unavailable" — `?mine=true` returns at least the caller's own org, so an
  //                   empty list once loaded means the request failed. Say so:
  //                   the gated empty answer reads as "no teachers".
  //   "network"     — >1 school in the selected subtree; read schools.
  //   "school"      — one school; read teachers.
  let scopeMode = "loading";
  if (loaded) {
    if (organizations.length === 0) scopeMode = "unavailable";
    else scopeMode = isNetwork ? "network" : "school";
  }

  return {
    canCompareSchools,
    scopeMode,
    organizations,
    effectiveOrgUuid,
    // What the user actually PICKED, as opposed to effectiveOrgUuid's fallback
    // to a staff link: an endpoint that spans the caller's whole read scope
    // when un-narrowed must not be handed a guess.
    selectedOrgUuid,
    setSelectedOrgUuid,
    pickedOrgUuid,
    pickPending,
    // `uuid` and every in-scope org under it.
    subtreeOf,
  };
}
